import { IconChevronLeft, IconCoffee, IconPhone, IconPlus } from "@tabler/icons-react";
import { CSSProperties, Fragment, ReactNode } from "react";
import { Link } from "react-router-dom";
import classes from "./home.module.scss";

export type UpcomingItem = {
  key: string;
  time: string;
  duration: string;
  customer: string;
  detail: string;
  // undefined hides the staff dot (a stylist only sees their own appointments).
  staffColor?: string | null;
  phone?: string | null;
  chip?: { text: string; tone: "soon" | "live" | "day" };
  // Free time right before this visit, shown as a quiet row above it; `to` books into it.
  gapBefore?: { from: string; text: string; to?: string };
};

type Props = {
  title: string;
  meta?: string;
  items: UpcomingItem[];
  moreText?: string;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  empty: ReactNode;
};

export default function UpcomingAppointments({ title, meta, items, moreText, isLoading, isError, onRetry, empty }: Props) {
  return (
    <section className={classes.section} aria-labelledby="home-upcoming-title">
      <div className={classes.sectionHead}>
        <div className={classes.sectionTitleRow}>
          <h2 id="home-upcoming-title" className={classes.sectionTitle}>
            {title}
          </h2>
          {meta && <span className={classes.sectionMeta}>{meta}</span>}
        </div>
        <Link to="/dashboard/calendar" className={classes.textLink}>
          تقویم
          <IconChevronLeft size={16} stroke={1.7} aria-hidden />
        </Link>
      </div>
      <div className={classes.card} aria-busy={isLoading || undefined}>
        {isLoading ? (
          [0, 1].map((row) => (
            <div key={row} className={classes.appointment}>
              <div className={classes.appointmentMain}>
                <span className={classes.shimmerLight} style={{ width: 44, height: 30 }} />
                <span className={classes.appointmentInfo}>
                  <span className={classes.shimmerLight} style={{ width: "45%", height: 12 }} />
                  <span className={classes.shimmerLight} style={{ width: "70%", height: 10 }} />
                </span>
              </div>
            </div>
          ))
        ) : isError ? (
          <div className={classes.empty}>
            <span className={classes.emptyTitle}>نوبت‌ها دریافت نشد</span>
            <button type="button" className={classes.softAction} onClick={onRetry}>
              تلاش دوباره
            </button>
          </div>
        ) : items.length === 0 ? (
          empty
        ) : (
          <>
            {items.map((item) => (
              <Fragment key={item.key}>
                {item.gapBefore && <GapRow {...item.gapBefore} />}
                <div className={classes.appointment}>
                  <Link to="/dashboard/calendar" className={classes.appointmentMain}>
                    <span className={classes.time}>
                      <strong>{item.time}</strong>
                      {item.duration && <span>{item.duration}</span>}
                    </span>
                    <span className={classes.separator} aria-hidden />
                    <span className={classes.appointmentInfo}>
                      <span className={classes.customerLine}>
                        <span className={classes.customer}>{item.customer}</span>
                        {item.chip && (
                          <span className={classes.chip} data-tone={item.chip.tone}>
                            {item.chip.text}
                          </span>
                        )}
                      </span>
                      <span className={classes.detailLine}>
                        {item.staffColor !== undefined && (
                          <span
                            className={classes.staffDot}
                            style={item.staffColor ? ({ "--staff-color": item.staffColor } as CSSProperties) : undefined}
                            aria-hidden
                          />
                        )}
                        <span>{item.detail}</span>
                      </span>
                    </span>
                  </Link>
                  {item.phone && (
                    <a href={`tel:${item.phone}`} className={classes.call} aria-label={`تماس با ${item.customer}`}>
                      <IconPhone size={20} stroke={1.7} aria-hidden />
                    </a>
                  )}
                </div>
              </Fragment>
            ))}
            {moreText && (
              <Link to="/dashboard/calendar" className={classes.more}>
                {moreText}
                <IconChevronLeft size={16} stroke={1.7} aria-hidden />
              </Link>
            )}
          </>
        )}
      </div>
    </section>
  );
}

function GapRow({ from, text, to }: NonNullable<UpcomingItem["gapBefore"]>) {
  const content = (
    <>
      <span className={classes.gapTime}>{from}</span>
      <span className={classes.gapText}>
        <IconCoffee size={16} stroke={1.7} aria-hidden />
        <span>{text}</span>
        {to && (
          <span className={classes.gapAction}>
            <IconPlus size={14} stroke={2.2} aria-hidden />
            نوبت
          </span>
        )}
      </span>
    </>
  );
  return to ? (
    <Link to={to} className={classes.gapRow} aria-label={`ثبت نوبت در ${text} از ${from}`}>
      {content}
    </Link>
  ) : (
    <div className={classes.gapRow}>{content}</div>
  );
}

type EmptyProps = { title: string; text: string; actions?: ReactNode };

export function UpcomingEmpty({ title, text, actions }: EmptyProps) {
  return (
    <div className={classes.empty}>
      <span className={classes.emptyTitle}>{title}</span>
      <p className={classes.emptyText}>{text}</p>
      {actions && <div className={classes.emptyActions}>{actions}</div>}
    </div>
  );
}
